/**
 * PhoneCarousel Component
 *
 * iPhone mockup with vertical swipe carousel using Swiper.
 * Nav buttons use the same animated outline draw as Footer social icons.
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useActiveSlide } from "../../context/active-slide-context";
import VisuallyHidden from "../VisuallyHidden";
import Icon from "../Icon";
import phoneMockup from "../../images/social-mockup.png";

import "swiper/css";
import * as styles from "./PhoneCarousel.module.scss";

const draw = {
  hidden: { pathLength: 0.001, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.4, ease: "easeInOut" },
      opacity: { duration: 0.01 },
    },
  },
  exit: {
    pathLength: 0.001,
    opacity: 0,
    transition: {
      pathLength: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.01 },
    },
  },
};

function NavButton({ direction, onClick, children }) {
  const [isHovered, setIsHovered] = useState(false);
  const size = 44;
  const strokeWidth = 3;
  const r = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;

  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.navButton}
      data-direction={direction}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg
        className={styles.navButtonCircle}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        <motion.circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          variants={draw}
          initial="hidden"
          animate={isHovered ? "visible" : "exit"}
        />
      </svg>
      <span className={styles.navButtonIcon}>{children}</span>
    </button>
  );
}

const PhoneCarousel = ({ children, autoplay = false }) => {
  const { setActiveIndex, isPaused } = useActiveSlide();
  const swiperRef = React.useRef(null);

  // Pause/resume autoplay based on context
  React.useEffect(() => {
    if (!swiperRef.current || !autoplay) return;
    if (isPaused) {
      swiperRef.current.autoplay?.stop();
    } else {
      swiperRef.current.autoplay?.start();
    }
  }, [isPaused, autoplay]);

  const swiperContainerStyles = {
    height: "96%",
    width: "92%",
    position: "absolute",
    bottom: "2%",
    top: "2%",
    left: "4%",
    right: "4%",
    borderRadius: "8%",
    backgroundColor: "black",
  };

  const childArray = React.Children.toArray(children);

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Swiper
          modules={autoplay ? [Autoplay] : []}
          onBeforeInit={swiper => {
            swiperRef.current = swiper;
          }}
          onSlideChange={swiper => {
            setActiveIndex(swiper.activeIndex);
          }}
          speed={350}
          slidesPerView={1}
          slidesPerGroup={1}
          direction="vertical"
          rewind={true}
          autoplay={
            autoplay ? { delay: 4000, disableOnInteraction: true } : false
          }
          style={swiperContainerStyles}
        >
          {React.Children.map(children, child => (
            <SwiperSlide>{child}</SwiperSlide>
          ))}
        </Swiper>
        <img
          loading="eager"
          src={phoneMockup}
          alt=""
          width={500}
          height={1019}
          className={styles.phone}
        />
      </div>
      {childArray.length > 1 && (
        <>
          <NavButton
            direction="previous"
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <Icon name="chevron" size="2.8rem" />
            <VisuallyHidden>Navigate to previous slide</VisuallyHidden>
          </NavButton>
          <NavButton
            direction="next"
            onClick={() => swiperRef.current?.slideNext()}
          >
            <Icon name="chevron" size="2.8rem" />
            <VisuallyHidden>Navigate to next slide</VisuallyHidden>
          </NavButton>
        </>
      )}
    </div>
  );
};

export default PhoneCarousel;
